import { AssetData } from '../../../utils/types';
import { extractTextFromJSX } from '../../SpaceAssetsSelectorModal/helper';

export const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};

export const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '-';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

export const getAssetTypeDisplay = (asset: AssetData): string => {
    if (asset.is_dir) return 'Folder';
    return asset.asset_type?.title || asset.content_type || '-';
};

export const filterNonDirectoryAssets = (assets: AssetData[]): AssetData[] => {
    return assets.filter(asset => !asset.is_dir);
};

export const createBreadcrumbFromAsset = (asset: AssetData) => {
    return {
        name: asset.title,
        root: false,
        folder_uid: asset.uid,
        ...asset
    };
};

export const isGroupValue = (value: any): boolean => {
    return typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).length > 0;
};

export const getChildrenCount = (groupData: Record<string, any>): number => {
    if (!groupData || typeof groupData !== 'object') return 0;
    return Object.keys(groupData).length;
};

export const formatMetadataValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return JSON.stringify(value);
};

export const hasMetadata = (metadata?: Record<string, any>): boolean => {
    return !!(metadata && Object.keys(metadata).length > 0);
};

export const UI_CONSTANTS = {
    ITEMS_PER_PAGE: 20,
    POPOVER_WIDTH: 330,
    MAX_VISIBLE_TAGS: 6,
    MAX_DISPLAY_COUNT: 10000
} as const;

export const generateContainerClassName = (
    showSidebar: boolean,
    isSidebarExpanded: boolean,
    hasAssets: boolean,
    isLoading: boolean
): string => {
    const classes = ['GalleryView__container'];

    if (showSidebar) {
        classes.push('GalleryView__container--with-sidebar');
        if (!isSidebarExpanded) classes.push('GalleryView__container--sidebar-collapsed');
    } else {
        classes.push('GalleryView__container--no-sidebar');
    }

    if (!hasAssets && !isLoading) {
        classes.push('GalleryView__container--empty');
    }

    return classes.join(' ');
};

export const formatDisplayCount = (count: number): string => {
    return count > UI_CONSTANTS.MAX_DISPLAY_COUNT
        ? `${UI_CONSTANTS.MAX_DISPLAY_COUNT}+`
        : String(count);
};

export const calculatePopoverPosition = (
    triggerRect: DOMRect,
    popoverWidth: number = UI_CONSTANTS.POPOVER_WIDTH,
): { top: number; left: number } => {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    
    const headerHeight = 60; 
    const maxContentHeight = 200; 
    const popoverHeight = headerHeight + maxContentHeight; 
    
    let top = triggerRect.top + scrollY;
    let left = triggerRect.left + scrollX - popoverWidth;
    
    if (left < scrollX + 16) {
        left = triggerRect.right + scrollX + 8;
        
        if (left + popoverWidth > scrollX + viewportWidth - 16) {
            left = scrollX + Math.max(16, (viewportWidth - popoverWidth) / 2);
        }
    }
    
    if (left + popoverWidth > scrollX + viewportWidth - 16) {
        left = scrollX + viewportWidth - popoverWidth - 16;
    }
    
    const triggerTop = triggerRect.top + scrollY;
    const triggerBottom = triggerRect.bottom + scrollY;
    
    let preferredTop = triggerTop;
    
    if (preferredTop + popoverHeight > scrollY + viewportHeight - 16) {
        preferredTop = triggerBottom - popoverHeight;
        
        if (preferredTop < scrollY + 16) {
            preferredTop = scrollY + 16;
        }
    }
    
    return { 
        top: preferredTop, 
        left: Math.max(scrollX + 16, left) 
    };
};

export const createSearchParamsString = (
    sortById: string,
    sortingDirection: string,
    searchText: string,
    searchType: string | React.ReactNode
): string => {
    return `${sortById}-${sortingDirection}-${searchText}-${extractTextFromJSX(searchType, 1)}`;
};

export const hasSearchParamsChanged = (
    currentParams: string,
    previousParams: string
): boolean => {
    return currentParams !== previousParams;
};

export const getSpaceUids = (
    selectedFolder: any,
    linkedSpaces: string[]
): string[] => {
    return selectedFolder ? [selectedFolder.space_uid] : linkedSpaces;
};